/**
 * BaselCrosswalkView -- the Basel-7 operational-resilience readiness crosswalk
 * (P330). Route: /readiness/basel.
 *
 * A lens over the P267 maturity readiness: it reprojects the live per-dimension
 * evidence onto the Basel Committee's 2021 seven operational-resilience
 * principles, showing per pillar what the twin PROVES vs what the bank ATTESTS.
 * Works today as a board/maturity tool; becomes the compliance surface the day
 * SAMA or CBUAE issues a dedicated regime, because every regime derives from
 * Basel-7. See docs/basel-opres-pillar-map.md.
 *
 * Hardcoded English to match the sibling shared/ exec views (readiness, board).
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import type { BaselCrosswalk, BaselPillar } from "shared/api/types";
import { useToast } from "shared/toast/ToastProvider";
import {
  downloadBaselCrosswalkPack,
  useBaselCrosswalk,
} from "shared/readiness/useReadiness";

const STATUS_TONE: Record<string, string> = {
  proved: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  strong: "bg-sky-100 text-sky-800 ring-sky-300",
  partial: "bg-amber-100 text-amber-800 ring-amber-300",
  thin: "bg-rose-100 text-rose-800 ring-rose-300",
};

/* ROUND 13 H, eleventh round -- an amber chip on a line of its own.

   Both occurrences on this page are a single word in a coloured box
   with nothing saying what it is a judgement OF or why it came out
   that way. The finding is closed on the portfolio and on the entity
   page; these two were the last of the class, and they were also
   hardcoded English literals that no catalogue could reach.

   Each word now carries the rule that produced it. */

const OVERALL_TONE: Record<string, string> = {
  ready: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  developing: "bg-amber-100 text-amber-800 ring-amber-300",
  early: "bg-rose-100 text-rose-800 ring-rose-300",
};

function EvidenceChips({
  label,
  items,
  tone,
}: {
  label: string;
  items: string[];
  tone: string;
}): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </span>
      {items.map((it) => (
        <span key={it} className={`rounded px-1.5 py-0.5 text-xs ${tone}`}>
          {it}
        </span>
      ))}
    </div>
  );
}

function PillarCard({ pillar }: { pillar: BaselPillar }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid={`basel-pillar-${pillar.number}`}
      className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t("screens.baselCrosswalkView.pillar")} {pillar.number}
          </div>
          <h3 className="font-display text-lg leading-snug text-slate-900">
            {pillar.name}
          </h3>
          <p className="mt-0.5 text-sm text-slate-500">{pillar.intent}</p>
        </div>
        <span
          data-testid={`basel-pillar-${pillar.number}-status`}
          data-status={pillar.status}
          title={t(`screens.baselCrosswalkView.pillarStatusWhy.${pillar.status}`, {
            defaultValue: "",
          })}
          className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ring-1 ${
            STATUS_TONE[pillar.status] ?? STATUS_TONE.thin
          }`}
        >
          {t(`screens.baselCrosswalkView.pillarStatus.${pillar.status}`, {
            defaultValue: pillar.status,
          })}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>
          <span className="font-semibold text-emerald-700">{pillar.proved_count}</span>{" "}
          {t("screens.baselCrosswalkView.provedByTheTwin")}
        </span>
        <span>
          <span className="font-semibold text-amber-700">{pillar.attested_count}</span>{" "}
          {t("screens.baselCrosswalkView.bankAttested")}
        </span>
        <span>
          <span className="font-semibold text-rose-700">{pillar.gap_count}</span>{" "}
          {t("screens.baselCrosswalkView.gap")}
        </span>
        <span className="tabular-nums">
          {t("screens.baselCrosswalkView.maturity")}{" "}
          <span className="font-semibold text-slate-700">
            {pillar.rolled_score.toFixed(1)}
          </span>
          /5
        </span>
      </div>

      <div className="mt-3 space-y-1.5">
        <EvidenceChips
          label="Proved"
          items={pillar.dimensions.flatMap((d) => d.proved)}
          tone="bg-emerald-50 text-emerald-800"
        />
        <EvidenceChips
          label="Attested"
          items={pillar.dimensions.flatMap((d) => d.attested)}
          tone="bg-amber-50 text-amber-800"
        />
        <EvidenceChips
          label="Gap"
          items={pillar.dimensions.flatMap((d) => d.gap)}
          tone="bg-rose-50 text-rose-800"
        />
      </div>

      {pillar.note ? (
        <p className="mt-3 border-s-2 border-brand-brass/50 ps-2 text-xs italic text-slate-500">
          {pillar.note}
        </p>
      ) : null}

      <div className="mt-2 text-[11px] text-slate-400">
        {t("screens.baselCrosswalkView.assessedVia")}{" "}
        {pillar.dimensions.map((d) => d.code).join(" + ")}{" "}
        {t("screens.baselCrosswalkView.ofTheMaturityModel")}
      </div>
    </div>
  );
}

function Body({ data }: { data: BaselCrosswalk }): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <section
        data-testid="basel-overall"
        className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span
            data-testid="basel-overall-status"
            data-status={data.overall_status}
            title={t(
              `screens.baselCrosswalkView.overallStatusWhy.${data.overall_status}`,
              { defaultValue: "" },
            )}
            className={`rounded px-3 py-1 text-lg font-semibold ring-1 ${
              OVERALL_TONE[data.overall_status] ?? OVERALL_TONE.early
            }`}
          >
            {t(`screens.baselCrosswalkView.overallStatus.${data.overall_status}`, {
              defaultValue: data.overall_status,
            })}
          </span>
          <span className="font-display text-3xl text-slate-900 tabular-nums">
            {Math.round(data.proved_share * 100)}%
          </span>
          <span className="text-sm text-slate-500">
            {t("screens.baselCrosswalkView.twinProvedCoverageAcrossTheSevenPillars")}
          </span>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          <span className="font-semibold text-emerald-700">{data.pillars_proved}</span>{" "}
          {t("screens.baselCrosswalkView.provedStrong")}{" "}
          <span className="font-semibold text-amber-700">{data.pillars_partial}</span>{" "}
          {t("screens.baselCrosswalkView.partial")}{" "}
          <span className="font-semibold text-rose-700">{data.pillars_thin}</span>{" "}
          {t("screens.baselCrosswalkView.thin")}
        </p>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {data.pillars.map((p) => (
          <PillarCard key={p.number} pillar={p} />
        ))}
      </div>
    </>
  );
}

export function BaselCrosswalkView(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useBaselCrosswalk();
  const toast = useToast();

  return (
    <article data-testid="basel-crosswalk-view" className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="font-display text-2xl text-slate-900">
            <MarsadIcon
              name="benchmark"
              className="me-2 inline-block h-6 w-6 align-[-4px]"
            />
            {t("screens.baselCrosswalkView.basel7OperationalResilienceReadiness", {
              framework: "Basel-7",
            })}
          </h1>
          <p className="max-w-3xl text-sm text-slate-500">
            {t(
              "screens.baselCrosswalkView.everyDedicatedOperationalResilienceRegimeThe",
            )}{" "}
            <span className="font-medium text-emerald-700">
              {t("screens.baselCrosswalkView.proves")}
            </span>{" "}
            {t("screens.baselCrosswalkView.versusWhatTheBank")}{" "}
            <span className="font-medium text-amber-700">
              {t("screens.baselCrosswalkView.attests")}
            </span>
            {t("screens.baselCrosswalkView.itIsALiveLensOverThe")}
          </p>
          <Link
            to="/readiness"
            className="inline-block text-sm text-brand-brassDark hover:underline"
          >
            {t("screens.baselCrosswalkView.backToTheMaturityReadiness")}
          </Link>
        </div>
        <button
          type="button"
          data-testid="basel-download-pack"
          onClick={() => {
            void downloadBaselCrosswalkPack().catch((e) =>
              toast.push({ tone: "error", message: String(e?.message ?? e) }),
            );
          }}
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10"
        >
          {t("screens.baselCrosswalkView.downloadSignedPack")}
        </button>
      </header>

      {isLoading ? (
        <div data-testid="basel-loading" className="text-sm text-slate-500">
          {t("screens.baselCrosswalkView.loading")}
        </div>
      ) : isError || !data ? (
        <div data-testid="basel-error" className="text-sm text-slate-500">
          {t("screens.baselCrosswalkView.readinessCrosswalkUnavailable")}
        </div>
      ) : (
        <Body data={data} />
      )}
    </article>
  );
}
